import { useEffect, useState } from 'react'
import { Button, Chip, MenuItem, Box, Typography, Menu, Dialog, DialogContent, DialogActions, Alert, Stack, IconButton, Tooltip } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { EyeOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons'
import { CheckCircleOutlineRounded, DownloadOutlined, InfoOutlined, MoneyOffOutlined, CancelOutlined, PendingOutlined, ErrorOutlined, WarningOutlined } from '@mui/icons-material'

import { useAuth } from 'services/hook/Auth'
import { useApiData } from 'services/hook/useApiData'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { PageHeader } from 'components/PageHeader'
import CellRenderer from 'components/list/CellRenderer'
import { getPermissionKeys } from 'utils/util'
import { formatPhoneNumber } from 'utils/helpers'
import DataGridList from 'components/data-grid/DataGridList'
import { CellMobileDetails } from './list/Payment.List.Cell'
import { PaymentListFilter } from './list/Payment.List.Filter'
import { paymentList, paymentUpdateStatus, exportInit, exportDownloadUrl } from '../../models/payment'
import { EVENT_PAYMENT_UPDATE_INIT, EVENT_PAYMENT_DELETE_INIT, EVENT_PAYMENT_UPDATE_SUCCESS } from '../../models/events/payment.event'
import DirhamIcon from '../../assets/images/dirham-symbol.png'
import { useSnackbar } from 'notistack'

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------
const PaymentList = () => {
    const permissionsKeys = getPermissionKeys('payment')
    return (
        <>
            <PageHeader
                title="Payments"
                breadcrumbs={[{ path: '#', label: 'Payments' }]}
            />
            <PaymentListTable permissionsKeys={permissionsKeys} />
        </>
    )
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------
const PaymentListTable = ({ permissionsKeys }) => {
    const eventbus = useEventBusContext()
    const is_mobile = useMediaQuery('(max-width:991px)')

    const initdata = { data: [], total: 0 }
    const initparams = { page: 0, limit: 10, sort: '', orderby: '', filters: {} }

    const desktopGridSx = {
        '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            lineHeight: 1.5,
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus, &:focus-within': { outline: 'none !important' },
        },
        '& .MuiDataGrid-row': {
            minHeight: '48px !important',
            borderBottom: '1px solid #e0e0e0 !important',
            '&:focus, &:focus-within': { outline: 'none !important' },
        },
        '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f5f5f5',
            fontSize: '1rem',
            fontWeight: 600,
            outline: 'none !important',
            '&:focus, &:focus-within': { outline: 'none !important' },
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '1rem',
            fontWeight: 600,
        },
    }

    const mobileGridSx = {
        '& .MuiDataGrid-cellCheckbox': { alignItems: 'center' },
        '& .MuiDataGrid-topContainer': { opacity: 0, visibility: 'hidden' },
        '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #c5c5c5a1 !important',
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
        },
    }

    const [data, params, loading, loadData] = useApiData(
        initdata,
        initparams,
        (params, setData) => {
            paymentList({ ...params, ...params.filters })
                .then((res) => setData(res))
                .catch((err) => console.error(err))
        },
        false
    )

    useEffect(() => {
        return eventbus.on(EVENT_PAYMENT_UPDATE_SUCCESS, () => {
            loadData(params)
        })
    }, [eventbus, params, loadData])

    return (
        <DataGridList
            rows={data.data}
            columns={is_mobile ? mobile_cols : desktop_cols}
            getRowHeight={() => 'auto'}
            actions={is_mobile ? null : <ListActions params={params} />}
            loading={loading}
            filterkey="paymentlist"
            sx={is_mobile ? mobileGridSx : desktopGridSx}
            checkboxSelection={false}
            columnHeaderHeight={is_mobile ? 0 : 56}
            filter={(filters, setFilters) => (
                <PaymentListFilter filters={filters} setFilters={setFilters} />
            )}
            onFilter={(values) => {
                const flat = Array.isArray(values) ? values.reduce((acc, f) => ({ ...acc, [f.field]: f.value }), {}) : values 
                params['filters'] = flat
                loadData(params)
                // params['filters'] = values
                // loadData(params)
            }}
            search={(query) => loadData({ ...params, query })}
            rowCount={data.total}
            paginationMode="server"
            paginationModel={{ page: params.page, pageSize: params.limit }}
            onPaginationModelChange={(pagination) => {
                if (
                    pagination.page === params.page &&
                    pagination.pageSize === params.limit
                ) return
                loadData({ ...params, page: pagination.page, limit: pagination.pageSize })
            }}
            sortingMode="server"
            onSortModelChange={(sortModel) => {
                if (sortModel.length > 0) {
                    loadData({ ...params, orderby: sortModel[0].field, sort: sortModel[0].sort })
                } else {
                    loadData({ ...params, orderby: '', sort: '' })
                }
            }}
            onInit={(pagination, sortModel, search, filters) => {
                let inparam = { query: search, filters: filters ?? {} }
                if (pagination) {
                    inparam = { ...inparam, page: pagination.page, limit: pagination.pageSize }
                }
                if (sortModel?.length > 0) {
                    inparam = { ...inparam, orderby: sortModel[0].field, sort: sortModel[0].sort }
                }
                loadData({ ...params, ...inparam })
            }}
        />
    )
}

// ---------------------------------------------------------------------------
// Toolbar actions
// ---------------------------------------------------------------------------
const ListActions = ({ params = {} }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const [exporting, setExporting] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const open = Boolean(anchorEl)

    const handleExport = async (format) => {
        setAnchorEl(null)
        setExporting(true)

        try {
            const res = await exportInit({ format, filters: params.filters ?? {} })
            const key = res?.key ?? res?.data?.key
            if (!key) throw new Error('No export key received')

            const API_BASE = import.meta.env.VITE_API_URL
            const fileRes = await fetch(`${API_BASE}/payment/export/download/${key}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': '*/*',
                },
            })

            if (!fileRes.ok) throw new Error(`Download failed: ${fileRes.status}`)

            const blob = await fileRes.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `payments-${format === 'excel' ? 'export.xlsx' : 'export.pdf'}`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)

            enqueueSnackbar(`${format.toUpperCase()} exported successfully`, {
                variant: 'success',
                autoHideDuration: 3000,
            })
        } catch (err) {
            console.error('Export failed:', err)
            enqueueSnackbar(err?.message ?? 'Export failed. Please try again.', {
                variant: 'error',
                autoHideDuration: 4000,
            })
        } finally {
            setExporting(false)
        }
    }

    return (
        <Stack direction="row" spacing={1}>
            <Button
                variant="outlined"
                color="primary"
                disabled={exporting}
                endIcon={<DownloadOutlined />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {exporting ? 'Exporting...' : 'Export'}
            </Button>

            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => handleExport('excel')}>
                    <FileExcelOutlined style={{ color: '#389e0d', marginRight: 8 }} />
                    Export as Excel
                </MenuItem>
                <MenuItem onClick={() => handleExport('pdf')}>
                    <FilePdfOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                    Export as PDF
                </MenuItem>
            </Menu>
        </Stack>
    )
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------
const STATUS_OPTIONS = [
    { key: 'pending', label: 'Pending' },
    { key: 'paid', label: 'Paid' },
    { key: 'failed', label: 'Failed' },
    { key: 'refunded', label: 'Refunded' },
    { key: 'cancelled', label: 'Cancelled' },
]

const getStatusStyles = (status) => {
    switch (String(status).toLowerCase()) {
        case 'pending': return { bgColor: '#fff1b8', fgColor: '#d48806', icon: <PendingOutlined /> }
        case 'paid': return { bgColor: '#f6ffed', fgColor: '#389e0d', icon: <CheckCircleOutlineRounded /> }
        case 'failed': return { bgColor: '#f9e3e1', fgColor: '#cf1322', icon: <ErrorOutlined /> }
        case 'refunded': return { bgColor: '#e6f7ff', fgColor: '#096dd9', icon: <MoneyOffOutlined /> }
        case 'cancelled': return { bgColor: '#f9e3e1', fgColor: '#cf1322', icon: <CancelOutlined /> }
        default: return { bgColor: '#f5f5f5', fgColor: '#595959', icon: <InfoOutlined /> }
    }
}

const CellStatus = ({ item }) => {
    const eventbus = useEventBusContext()
    const [loading, setLoading] = useState(false)
    const [currentStatus, setCurrentStatus] = useState(item.status)
    const [anchorEl, setAnchorEl] = useState(null)
    const [confirmDialog, setConfirmDialog] = useState(null) // 'refund' | 'cancel' | null
    const [pendingStatus, setPendingStatus] = useState(null)

    const FINAL = ['paid', 'refunded', 'cancelled', 'failed']
    const isFinal = FINAL.includes(currentStatus)

    useEffect(() => { setCurrentStatus(item.status) }, [item.status])

    const handleChipClick = (e) => {
        e.stopPropagation()
        if (loading || isFinal) return
        setAnchorEl(e.currentTarget)
    }

    const handleSelectStatus = (key) => {
        setAnchorEl(null)
        if (key === 'refunded' || key === 'cancelled') {
            setPendingStatus(key)
            setConfirmDialog(key)
            return
        }
        applyStatus(key)
    }

    const applyStatus = async (key) => {
        setLoading(true)
        try {
            const res = await paymentUpdateStatus(item.id, key)
            const next = res?.data?.status ?? key
            setCurrentStatus(next)
            eventbus.fire(EVENT_PAYMENT_UPDATE_SUCCESS, { ...item, status: next })
        } catch (err) {
            console.error('Failed to update payment status:', err)
        } finally {
            setLoading(false)
        }
    }

    const { bgColor, fgColor, icon } = getStatusStyles(currentStatus)
    const label = STATUS_OPTIONS.find(o => o.key === currentStatus)?.label ?? currentStatus

    return (
        <>
            <Chip
                label={label}
                size="medium"
                // onClick={handleChipClick}
                disabled={loading || isFinal}
                sx={{
                    mt: '5px',
                    color: fgColor,
                    fontSize: { xs: 14, xl: 15 },
                    backgroundColor: bgColor,
                    cursor: loading ? 'wait' : isFinal ? 'default' : 'pointer',
                    opacity: '1 !important',
                    transition: 'all 0.2s ease',
                }}
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                onClick={(e) => e.stopPropagation()}
            >
                {STATUS_OPTIONS.map((opt) => (
                    <MenuItem
                        key={opt.key}
                        selected={opt.key === currentStatus}
                        onClick={() => handleSelectStatus(opt.key)}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* Refund confirmation */}
            <Dialog
                open={confirmDialog === 'refunded'}
                onClose={() => setConfirmDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent dividers sx={{ px: 4, py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <MoneyOffOutlined style={{ fontSize: 48, color: '#096dd9' }} />
                        <Typography variant="h5" textAlign="center">
                            Mark this payment as refunded?
                        </Typography>
                        <Alert severity="info" sx={{ width: '100%' }}>
                            This will record the payment as refunded. This action cannot be undone.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button variant="outlined" color="secondary" onClick={() => setConfirmDialog(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        disabled={loading}
                        onClick={() => { setConfirmDialog(null); applyStatus('refunded') }}
                    >
                        {loading ? 'Updating...' : 'Yes, Mark as Refunded'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel confirmation */}
            <Dialog
                open={confirmDialog === 'cancelled'}
                onClose={() => setConfirmDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent dividers sx={{ px: 4, py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <WarningOutlined style={{ fontSize: 48, color: '#cf1322' }} />
                        <Typography variant="h5" textAlign="center">
                            Cancel this payment?
                        </Typography>
                        <Alert severity="warning" sx={{ width: '100%' }}>
                            This action cannot be undone. The payment will be marked as cancelled.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button variant="outlined" color="secondary" onClick={() => setConfirmDialog(null)}>
                        No, Keep Payment
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={loading}
                        onClick={() => { setConfirmDialog(null); applyStatus('cancelled') }}
                    >
                        {loading ? 'Cancelling...' : 'Yes, Cancel Payment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—'
    return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <img src={DirhamIcon} width={16} alt="AED" />
            {parseFloat(amount)}
        </span>
    )
}

const getPaymentPropsData = (props) => ({
    actions: {
        detail: {
            tooltip: 'View Payment',
            menu: 'View Details',
            icon: <EyeOutlined />,
            link: `/payment/${props.id}`,
        },
    },
    events: {
        update_init: EVENT_PAYMENT_UPDATE_INIT,
        delete_init: EVENT_PAYMENT_DELETE_INIT,
    },
})

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const mobile_cols = [
    {
        field: 'tran_ref',
        headerName: 'Detail',
        flex: 1,
        renderCell: (props) => (
            <CellMobileDetails item={props.row} propsDetails={getPaymentPropsData(props.row)} />
        ),
    },
]

const desktop_cols = [
    { field: 'tran_ref', headerName: 'Ref #', flex: 0.8, minWidth: 160, renderCell: (props) => ( <CellRenderer type="text" item={props.row} field="tran_ref" propsDetails={getPaymentPropsData(props.row)}/> ), },
    { field: 'order_number', headerName: 'Order', flex: 0.8, minWidth: 190, renderCell: (props) => ( <Typography variant="h4" sx={{ fontSize: { xs: 14, xl: 16 }, fontWeight: 400 }}> {props.row.order?.order_number ?? '—'} </Typography> ), },
    { field: 'customer_name', headerName: 'Customer / WhatsApp', flex: 1.2, minWidth: 190,
        renderCell: (props) => (
            <Stack spacing={0.25}>
                <Typography variant="h4" sx={{fontSize: { xs: 15, xl: 16 }, fontWeight: '400'}}>{props.row.order.customer?.name || '—'}</Typography>
                <Typography variant="h4" sx={{fontSize: { xs: 15, xl: 16 }, fontWeight: '400'}} >{formatPhoneNumber(props.row.order.customer?.whatsapp_number) || '—'}</Typography>
            </Stack>
        )
    },
    { field: 'amount', headerName: 'Amount', flex: 0.7, minWidth: 100, renderCell: (props) => ( <Typography variant="h6" fontWeight={600}> {formatCurrency(props.row.amount)} </Typography> ), },
    { field: 'status', headerName: 'Status', flex: 0.7, minWidth: 100, maxWidth: 160, renderCell: (props) => <CellStatus item={props.row} />, }, 
    { field: 'created_at', headerName: 'Date', flex: 0.8, minWidth: 120, maxWidth: 140, renderCell: (props) => ( <CellRenderer type="date" item={props.row} field="created_at" /> ), }, 
    { field: 'action', headerName: 'Action', sortable: false, flex: 0.4, minWidth: 80, renderCell: (props) => ( <CellRenderer type="action" item={props.row} propsDetails={getPaymentPropsData(props.row)}/> ), },
]

export default PaymentList